import { useTranslation } from '@portals-platform/i18n';
import { Form, FormErrorSummary, useForm, useWatch } from '@portals-platform/ui-forms';
import { useNavigate } from '@tanstack/react-router';
import { useMemo } from 'react';
import type { CertificateResponse } from '../../data-access/api/responses.js';
import { useCertificateTemplatesByCountry } from '../../data-access/index.js';
import { useTranslatedLabels } from '../../hooks/use-translated-labels.js';
import { getInitialValues } from '../../lib/new-certificate/defaults.js';
import { FIELD_LABEL_KEYS } from '../../lib/new-certificate/fields.js';
import { useFieldsBySection } from '../../lib/new-certificate/hooks/use-fields-by-section.js';
import { useResolver } from '../../lib/new-certificate/hooks/use-resolver.js';
import { useSubmitCertificate } from '../../lib/new-certificate/hooks/use-submit-certificate.js';
import { mapCertificateToFormValues } from '../../lib/new-certificate/mappers.js';
import type { FormValues } from '../../lib/new-certificate/types.js';
import { ServerErrorAlert } from '../server-error-alert.js';
import { CertificateFormActions } from './certificate-form-actions.js';
import { AdditionalInfoSection } from './sections/additional-info-section.js';
import { ComplianceSection } from './sections/compliance-section.js';
import { CoverageLimitsSection } from './sections/coverage-limits-section.js';
import { CustomerInformationSection } from './sections/customer-information-section.js';
import { PolicyDetailsSection } from './sections/policy-details-section.js';
import { SituationSection } from './sections/situation-section.js';
import { StorageCoverageSection } from './sections/storage-coverage-section.js';
import { TemplateSection } from './sections/template-section.js';

interface CertificateFormProps {
  /** When set, the form edits this certificate instead of creating a new one. */
  initialCertificate?: CertificateResponse;
}

export function CertificateForm({ initialCertificate }: CertificateFormProps = {}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const errorLabels = useTranslatedLabels(FIELD_LABEL_KEYS);
  const isEditMode = initialCertificate !== undefined;

  const { resolver, templatesRef } = useResolver();
  const defaultValues = useMemo(
    () =>
      initialCertificate ? mapCertificateToFormValues(initialCertificate) : getInitialValues(),
    [initialCertificate],
  );
  const form = useForm<FormValues>({
    resolver,
    mode: 'onTouched',
    defaultValues,
  });

  const [countryCode, certificateTemplateId] = useWatch({
    control: form.control,
    name: ['countryCode', 'certificateTemplateId'],
  });
  const { templates, isLoading: templatesLoading } = useCertificateTemplatesByCountry(countryCode);
  templatesRef.current = templates;

  const selectedTemplate = templates.find((t) => t.id === certificateTemplateId) ?? null;
  const fieldsBySection = useFieldsBySection(selectedTemplate);

  const { submit, saveChanges, serverError } = useSubmitCertificate(form, initialCertificate);

  return (
    <Form form={form} onSubmit={isEditMode ? saveChanges : submit(true)} className="gap-6">
      <ServerErrorAlert
        title={t(isEditMode ? 'certificates.edit.serverError' : 'certificates.new.serverError')}
        error={serverError}
      />

      <CustomerInformationSection />
      <TemplateSection templates={templates} isLoading={templatesLoading} />
      {selectedTemplate !== null && (
        <>
          <PolicyDetailsSection hasStorageCoverage={fieldsBySection.storageCoverage.length > 0} />
          {fieldsBySection.situation.length > 0 && (
            <SituationSection fieldKeys={fieldsBySection.situation} />
          )}
          {fieldsBySection.coverageLimits.length > 0 && (
            <CoverageLimitsSection fieldKeys={fieldsBySection.coverageLimits} />
          )}
          {fieldsBySection.storageCoverage.length > 0 && <StorageCoverageSection />}
          {fieldsBySection.compliance.length > 0 && <ComplianceSection />}
          <AdditionalInfoSection />

          <FormErrorSummary labels={errorLabels} />
          <CertificateFormActions
            isEditMode={isEditMode}
            onSaveDraft={() => void form.handleSubmit(submit(false))()}
            onCancel={
              initialCertificate
                ? () =>
                    void navigate({
                      to: '/certificates/$certificateId',
                      params: { certificateId: initialCertificate.id },
                    })
                : undefined
            }
          />
        </>
      )}
    </Form>
  );
}
